// src/utils/questionnaireQuestions/questionnaireScores.js
/**
 * Reportable totals for the questionnaires the backend scores.
 *
 * questionnaire-service sums every numeric response into `score`
 * (`computeQuestionnaireScore`), but it never reports a maximum — so the
 * denominator has to be stated here, where the question sets are defined.
 *
 * Maxima are written out rather than derived from the question modules on
 * purpose: `weekly-check-in` gates three of its five questions behind the
 * user's respiratory-condition variant, so summing every defined question
 * would overstate the total that any single user can actually reach.
 *
 * Not listed, and deliberately so:
 * • `sgrq` and `monthly-check-in` — the service does not sum a score for
 *   either, so there is nothing to show.
 */

export const QUESTIONNAIRE_MAX_SCORES = Object.freeze({
  // 8 questions, 0–5 each.
  cat: 40,
  // Same question set as CAT, regrouped.
  caat: 40,
  // 5 questions, 0–10 each.
  "r-scale": 50,
  // 5 questions, 0–4 each.
  act: 20,
  // 5 weighted questions, 0–2 each.
  "copd-ps": 10,
  // 2 questions everyone answers plus exactly 1 variant-gated question, 0–3
  // each. "NA" options are not numeric and contribute 0.
  "weekly-check-in": 9,
});

/**
 * The highest score a user can reach on `questionnaireId`.
 *
 * @param {string} questionnaireId
 * @returns {number|null} the total, or null when the questionnaire is unscored
 */
export function getQuestionnaireMaxScore(questionnaireId) {
  const key =
    typeof questionnaireId === "string"
      ? questionnaireId.trim().toLowerCase()
      : "";

  return QUESTIONNAIRE_MAX_SCORES[key] ?? null;
}

/**
 * Whether the active study has explicitly approved a questionnaire score for
 * patient presentation. Missing or malformed assignment data is deliberately
 * treated as not approved.
 *
 * @param {string} questionnaireId
 * @param {Array<{questionnaireId?: string, showScoreToPatient?: boolean}>} assignments
 * @returns {boolean}
 */
export function shouldShowQuestionnaireScoreToPatient(
  questionnaireId,
  assignments,
) {
  const normalizedQuestionnaireId =
    typeof questionnaireId === "string"
      ? questionnaireId.trim().toLowerCase()
      : "";

  if (!normalizedQuestionnaireId || !Array.isArray(assignments)) {
    return false;
  }

  return assignments.some(
    (assignment) =>
      typeof assignment?.questionnaireId === "string" &&
      assignment.questionnaireId.trim().toLowerCase() ===
        normalizedQuestionnaireId &&
      assignment.showScoreToPatient === true,
  );
}

/**
 * Whether a completed questionnaire has a score worth showing.
 *
 * @param {string} questionnaireId
 * @param {unknown} score
 * @param {boolean} showScoreToPatient
 * @returns {boolean}
 */
export function hasReportableScore(
  questionnaireId,
  score,
  showScoreToPatient = false,
) {
  return (
    showScoreToPatient === true &&
    typeof score === "number" &&
    Number.isFinite(score) &&
    getQuestionnaireMaxScore(questionnaireId) !== null
  );
}
